import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Wifi,
  WifiOff,
  Clock,
  User,
  DollarSign,
  CalendarDays,
} from 'lucide-react';

export default function CalendarPage() {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [halaxyConnected, setHalaxyConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      window.location.href = '/admin-login';
      return;
    }
    checkHalaxyStatus();
    fetchAppointments();
  }, [currentWeekStart]);

  const checkHalaxyStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/halaxy/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setHalaxyConnected(data.connected);
      }
    } catch {
      setHalaxyConnected(false);
    }
  };

  const fetchAppointments = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/halaxy/appointments?start=${currentWeekStart.toISOString()}&end=${weekEnd.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
      } else {
        setAppointments([]);
      }
    } catch {
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    if (!halaxyConnected) {
      toast.error('Halaxy not connected');
      return;
    }
    setIsSyncing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/halaxy/sync`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        toast.success('Calendar synced from Halaxy');
        fetchAppointments();
      }
    } catch {
      toast.error('Sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    setCurrentWeekStart(newStart);
  };

  const goToToday = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getPaymentColor = (appointment) => {
    if (!appointment.is_assessment_patient) return 'grey';
    
    // Use per-appointment invoice data if available, otherwise use patient-level totals
    const invoiced = appointment.appt_invoice_gross || appointment.total_invoiced || 0;
    const paid = appointment.appt_invoice_paid || appointment.total_paid || 0;
    const owing = appointment.appt_invoice_balance || appointment.total_owing || 0;
    
    // If there's actual invoice data to work with
    if (invoiced > 0 || paid > 0 || owing > 0) {
      if (owing <= 0 && paid > 0) return 'green';   // Fully paid
      if (paid > 0 && owing > 0) return 'orange';   // Partial payment
      if (owing > 0 && paid === 0) return 'red';    // Unpaid
    }
    
    // Assessment patient with no billing data yet = Unpaid (they owe money, just not invoiced yet)
    return 'red';
  };

  const colorStyles = {
    green: {
      bg: 'bg-[#D4E4CE] border-l-4 border-l-[#5A8A3C]',
      text: 'text-[#2D5A1E]',
      label: 'Paid',
      dot: 'bg-[#5A8A3C]',
    },
    orange: {
      bg: 'bg-[#FBF0E4] border-l-4 border-l-[#D4A574]',
      text: 'text-[#8B6B4A]',
      label: 'Partial',
      dot: 'bg-[#D4A574]',
    },
    red: {
      bg: 'bg-[#FDE8E4] border-l-4 border-l-[#C17B6B]',
      text: 'text-[#8B3A2E]',
      label: 'Unpaid',
      dot: 'bg-[#C17B6B]',
    },
    grey: {
      bg: 'bg-[#F5F1E8] border-l-4 border-l-[#C8C3BC]',
      text: 'text-[#6B6560]',
      label: 'Regular',
      dot: 'bg-[#C8C3BC]',
    },
  };

  const weekDays = getWeekDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatWeekRange = () => {
    const end = new Date(currentWeekStart);
    end.setDate(end.getDate() + 6);
    const opts = { day: 'numeric', month: 'short' };
    const yearOpts = { day: 'numeric', month: 'short', year: 'numeric' };
    return `${currentWeekStart.toLocaleDateString('en-AU', opts)} - ${end.toLocaleDateString('en-AU', yearOpts)}`;
  };

  const getAppointmentsForDay = (date) => {
    return appointments.filter((appt) => {
      const apptDate = new Date(appt.start_time);
      return apptDate.toDateString() === date.toDateString();
    }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  };

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleTimeString('en-AU', { 
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: 'Australia/Brisbane'
      });
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8" data-testid="calendar-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Calendar - Navigating Minds</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-3xl lg:text-4xl font-semibold text-[#3A3530] font-heading" data-testid="calendar-title">
              Calendar
            </h1>
            <p className="mt-1 text-[#6B6560]">Weekly appointment view from Halaxy</p>
          </div>
          <div className="flex items-center gap-3 mt-4 sm:mt-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
              halaxyConnected ? 'bg-[#D4E4CE] text-[#3A5A2E]' : 'bg-[#F5F1E8] text-[#9B9590]'
            }`} data-testid="calendar-halaxy-status">
              {halaxyConnected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {halaxyConnected ? 'Halaxy Connected' : 'Halaxy Not Connected'}
            </div>
            <Button
              onClick={handleSync}
              disabled={isSyncing || !halaxyConnected}
              variant="outline"
              size="sm"
              className="border-[#8B9D83] text-[#6B7D63] hover:bg-[#8B9D83] hover:text-white disabled:opacity-50"
              data-testid="calendar-sync-btn"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              Sync
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mb-6 px-1">
          {Object.entries(colorStyles).map(([key, style]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <div className={`w-3 h-3 rounded-full ${style.dot}`} />
              <span className="text-[#6B6560]">{style.label}</span>
            </div>
          ))}
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-6 bg-white rounded-xl border border-[#E8E4DC] px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateWeek(-1)}
            className="text-[#6B6560] hover:bg-[#F5F1E8]"
            data-testid="calendar-prev-week"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              className="border-[#E8E4DC] text-[#6B6560] hover:bg-[#F5F1E8]"
              data-testid="calendar-today-btn"
            >
              Today
            </Button>
            <h2 className="text-lg font-semibold text-[#3A3530]" data-testid="calendar-week-range">
              {formatWeekRange()}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateWeek(1)}
            className="text-[#6B6560] hover:bg-[#F5F1E8]"
            data-testid="calendar-next-week"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Calendar Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-[#8B9D83] border-r-transparent"></div>
              <p className="mt-3 text-sm text-[#9B9590]">Loading calendar...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-3" data-testid="calendar-grid">
            {weekDays.map((day, index) => {
              const dayAppointments = getAppointmentsForDay(day);
              const isToday = day.toDateString() === today.toDateString();
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;

              return (
                <div
                  key={index}
                  className={`rounded-xl border overflow-hidden min-h-[400px] flex flex-col ${
                    isToday
                      ? 'border-[#8B9D83] bg-white shadow-md ring-1 ring-[#8B9D83]/20'
                      : isWeekend
                      ? 'border-[#E8E4DC] bg-[#FDFBF7]/50'
                      : 'border-[#E8E4DC] bg-white'
                  }`}
                  data-testid={`calendar-day-${index}`}
                >
                  {/* Day Header */}
                  <div className={`px-3 py-2.5 border-b text-center ${
                    isToday
                      ? 'bg-[#8B9D83] border-[#8B9D83]'
                      : 'bg-[#F5F1E8] border-[#E8E4DC]'
                  }`}>
                    <div className={`text-xs font-medium ${
                      isToday ? 'text-white/80' : 'text-[#9B9590]'
                    }`}>
                      {day.toLocaleDateString('en-AU', { weekday: 'short' })}
                    </div>
                    <div className={`text-lg font-bold ${
                      isToday ? 'text-white' : 'text-[#3A3530]'
                    }`}>
                      {day.getDate()}
                    </div>
                  </div>

                  {/* Appointments */}
                  <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                    {dayAppointments.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-xs text-[#C8C3BC]">No appointments</p>
                      </div>
                    ) : (
                      dayAppointments.map((appt, apptIndex) => {
                        const color = getPaymentColor(appt);
                        const style = colorStyles[color];
                        return (
                          <div
                            key={appt.id || apptIndex}
                            className={`rounded-lg p-2.5 ${style.bg} transition-all duration-150 hover:shadow-sm cursor-default`}
                            data-testid={`appointment-card-${appt.id || apptIndex}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-semibold ${style.text}`}>
                                {formatTime(appt.start_time)}
                              </span>
                              <Badge className={`text-[10px] px-1.5 py-0 rounded-full ${style.bg} ${style.text} border-0`}>
                                {style.label}
                              </Badge>
                            </div>
                            <div className={`text-sm font-medium ${style.text} truncate`}>
                              {appt.patient_name || 'Unknown'}
                            </div>
                            {appt.appointment_type && (
                              <div className={`text-xs ${style.text} opacity-70 truncate mt-0.5`}>
                                {appt.appointment_type}
                              </div>
                            )}
                            {appt.is_assessment_patient && (appt.appt_invoice_balance > 0 || appt.total_owing > 0) && (
                              <div className={`text-xs font-semibold ${style.text} mt-1`}>
                                Owing: ${(appt.appt_invoice_balance || appt.total_owing)?.toFixed(0)}
                              </div>
                            )}
                            {appt.is_assessment_patient && (appt.appt_invoice_balance || appt.total_owing || 0) <= 0 && (appt.appt_invoice_paid > 0 || appt.total_paid > 0) && (
                              <div className={`text-xs ${style.text} opacity-70 mt-1`}>
                                Paid in full
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state when Halaxy not connected */}
        {!halaxyConnected && appointments.length === 0 && !isLoading && (
          <div className="mt-8 text-center">
            <Card className="max-w-md mx-auto border-[#E8E4DC] border-dashed">
              <CardContent className="py-12">
                <WifiOff className="h-12 w-12 text-[#C8C3BC] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#3A3530] mb-2">Halaxy Not Connected</h3>
                <p className="text-sm text-[#9B9590] mb-4">
                  Connect your Halaxy account to see appointments here. Your calendar will automatically sync bookings and payment status.
                </p>
                <p className="text-xs text-[#C8C3BC]">
                  Provide your Halaxy API Client ID and Secret to get started.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
